/**
 * Pairing Gateway Port (Interface)
 *
 * Defines the contract for NFC product pairing operations with immediate workflow.
 * This interface enables dependency injection and testing with fake implementations.
 */

/**
 * Product information returned from pairing operations
 */
export interface ProductInfo {
  /** Shopify product ID */
  productId: string;
  /** Product title/name */
  title: string;
  /** Product image URL */
  imageUrl: string;
  /** Total edition size (e.g., 100 in "1/100") */
  editionSize?: number;
}

/**
 * Result of successful immediate pairing
 */
export interface PairingResult {
  /** Unique identifier for the linked NFC item */
  linkedNfcId: string;
  /** Product information */
  productInfo: ProductInfo;
  /** ISO8601 timestamp when pairing occurred */
  pairedAt: string;
  /** Sequential item number from NFC URL (e.g., 1 in "1/100") */
  itemNumber?: number;
}

/**
 * Two-word code for immediate pairing
 */
export interface PairingCode {
  /** First word of the BIP39 code */
  first: string;
  /** Second word of the BIP39 code */
  second: string;
}

/**
 * Pairing error types from API
 */
export type PairingErrorCode =
  | 'InvalidSignature'
  | 'ProductStolen'
  | 'CodeUsed'
  | 'InvalidCode'
  | 'AuthenticationRequired'
  | 'NetworkError'
  | 'UnknownError';

/**
 * Pairing error with typed code
 */
export class PairingError extends Error {
  constructor(
    message: string,
    public code: PairingErrorCode
  ) {
    super(message);
    this.name = 'PairingError';
  }
}

/**
 * Owner NFC Scan Context Response Types (Discriminated Union)
 */

export interface ProductInfoContext {
  productId: string;
  title: string;
  description: string;
  imageUrl: string;
  variant: string;
  productType?: string;
}

export interface UnclaimedItemContextOwner {
  type: 'unclaimed';
  isAuthentic: boolean;
  nfcId: string;
  productInfo: ProductInfoContext;
  pairingInfo: {
    hasPendingRequest: boolean;
    currentUserHasPendingRequest: boolean;
    pendingRequestExpiresAt?: string; // ISO 8601
  };
}

export interface ClaimedItemContextOwner {
  type: 'claimed';
  isAuthentic: boolean;
  nfcId: string;
  productInfo: ProductInfoContext;
  ownerInfo: {
    userId: string;
    isOwnedByCurrentUser: boolean;
    visibility: 'public' | 'private';
  };
  status: 'active';
  scanCount: number;
}

export interface StolenItemContext {
  type: 'stolen';
  isAuthentic: boolean;
  nfcId: string;
  productInfo: ProductInfoContext;
  reportedAt: string; // ISO 8601
}

export interface LostItemContext {
  type: 'lost';
  isAuthentic: boolean;
  nfcId: string;
  productInfo: ProductInfoContext;
  reportedAt: string;
}

export interface InvalidSignatureContext {
  type: 'invalidSignature';
  isAuthentic: false;
  nfcId: string;
  message: string;
}

export type OwnerNfcScanContextResponse =
  | UnclaimedItemContextOwner
  | ClaimedItemContextOwner
  | StolenItemContext
  | LostItemContext
  | InvalidSignatureContext;

/**
 * Pairing Gateway Interface
 *
 * Abstracts NFC product pairing operations for immediate workflow.
 * Implementations: HttpPairingGateway (production), FakePairingGateway (testing)
 */
export interface IPairingGateway {
  /**
   * Get scan context for NFC URL (determines routing based on item status)
   *
   * @param nfcUrl - Full NFC URL with signature
   * @returns Scan context with item status and routing information
   * @throws {PairingError} For network errors or API failures
   */
  getScanContext(nfcUrl: string): Promise<OwnerNfcScanContextResponse>;

  /**
   * Pair NFC product immediately with 2-word code
   *
   * @param nfcUrl - Full NFC URL with signature
   * @param code - Two-word BIP39 code from order email
   * @returns Pairing result with linkedNfcId and product info
   * @throws {PairingError} If signature invalid, product stolen, or code issues
   */
  pairImmediately(nfcUrl: string, code: PairingCode): Promise<PairingResult>;

  /**
   * Unpair item from Trophy Room
   *
   * Removes the item from the user's Trophy Room. This endpoint is planned
   * for Story 3.6.b (backend) and will be fully integrated in Story 3.6.d (frontend).
   *
   * @param itemId - Linked NFC item ID (linkedNfcId from pairing result)
   * @returns Promise that resolves when unpair is complete
   * @throws {PairingError} If not authenticated or item not found
   */
  unpairItem(itemId: string): Promise<void>;
}
